import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { directAnswerText } from '../ai/answer';
import { Icon } from './Icon';

export interface ConversationTurn {
  question: string;
  answer: string;
}

interface AiConversationProps {
  turns: readonly ConversationTurn[];
  loading: boolean;
  loadingQuestion: string;
  status: string;
  followUpQuestion: string;
  onFollowUpQuestionChange: (value: string) => void;
  onFollowUp: () => void;
  onClear: () => void;
  onStartNew: () => void;
}

const FOLLOW_UP_PRESETS = [
  { label: '依据是什么', value: '这个判断主要依据什么？' },
  { label: '说简单点', value: '可以再用更简单的话说明吗？' },
  { label: '要注意什么', value: '这件事接下来最需要注意什么？' }
] as const;

function answerParagraphs(value: string): string[] {
  return value
    .split(/\n+/)
    .map((line) => line.replace(/^\s*[-*]\s*/, '').trim())
    .filter(Boolean);
}

async function writeClipboard(value: string): Promise<boolean> {
  if (!value.trim() || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function TurnAnswer({ answer }: { answer: string }) {
  const [copyStatus, setCopyStatus] = useState('');
  const copyTimerRef = useRef<number | null>(null);
  const plainAnswer = directAnswerText(answer);
  const paragraphs = answerParagraphs(plainAnswer);

  useEffect(() => () => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
  }, []);

  async function copyAnswer() {
    setCopyStatus(await writeClipboard(plainAnswer || answer) ? '已复制' : '请手动复制');
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      copyTimerRef.current = null;
      setCopyStatus('');
    }, 2200);
  }

  return (
    <div className="ai-turn-answer">
      <div className="ai-turn-body">
        {(paragraphs.length ? paragraphs : ['本轮没有生成可显示的内容。']).map((paragraph, index) => (
          <p key={`${paragraph}-${index}`}>{paragraph}</p>
        ))}
      </div>
      <button type="button" className="ai-message-copy" onClick={() => void copyAnswer()}>
        <Icon name="copy" size={14} />{copyStatus || '复制本条'}
      </button>
    </div>
  );
}

function turnText(turn: ConversationTurn): string {
  return `问：${turn.question}\n答：${directAnswerText(turn.answer) || turn.answer}`;
}

export function AiConversation({
  turns,
  loading,
  loadingQuestion,
  status,
  followUpQuestion,
  onFollowUpQuestionChange,
  onFollowUp,
  onClear,
  onStartNew
}: AiConversationProps) {
  const [copyStatus, setCopyStatus] = useState('');
  const copyTimerRef = useRef<number | null>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const allText = turns.map(turnText).join('\n\n');

  useEffect(() => () => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
  }, []);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [loading, turns.length]);

  function showCopyStatus(message: string) {
    setCopyStatus(message);
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      copyTimerRef.current = null;
      setCopyStatus('');
    }, 2200);
  }

  async function copyAll() {
    if (!allText) return;
    showCopyStatus(await writeClipboard(allText) ? '已复制' : '请手动复制');
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!loading && followUpQuestion.trim()) onFollowUp();
  }

  return (
    <section className="ai-conversation" aria-label="连续问答">
      <header className="ai-conversation-header">
        <div>
          <span>同一盘面 · 上下文已关联</span>
          <h3>本次解读</h3>
        </div>
        {turns.length > 0 && (
          <div className="ai-conversation-actions" aria-label="对话操作">
            <button type="button" aria-label="复制对话" onClick={() => void copyAll()} disabled={!allText}><Icon name="copy" size={14} />{copyStatus || '复制'}</button>
            <button type="button" aria-label="清空对话" onClick={onClear} disabled={loading}>清空</button>
            <button type="button" onClick={onStartNew} disabled={loading}>重新解读</button>
          </div>
        )}
      </header>

      <div className="ai-conversation-list" role="log" aria-label="本次解读对话" aria-live="polite" aria-relevant="additions text">
        {turns.map((turn, index) => (
          <Fragment key={`${turn.question}-${index}`}>
            <article className="ai-message ai-message-user">
              <span className="ai-message-author">你</span>
              <p>{turn.question}</p>
            </article>
            <article className="ai-message ai-message-assistant">
              <span className="ai-message-avatar" aria-hidden="true">易</span>
              <div className="ai-message-content">
                <div className="ai-message-meta"><strong>易鉴</strong><span>结合当前盘面</span></div>
                <TurnAnswer answer={turn.answer} />
              </div>
            </article>
          </Fragment>
        ))}
        {loading && (
          <>
            <article className="ai-message ai-message-user">
              <span className="ai-message-author">你</span>
              <p>{loadingQuestion}</p>
            </article>
            <article className="ai-message ai-message-assistant ai-turn-loading" aria-busy="true">
              <span className="ai-message-avatar" aria-hidden="true">易</span>
              <div className="ai-message-content">
                <div className="ai-message-meta"><strong>易鉴</strong><span>正在回答</span></div>
                <p className="ai-turn-loading-text" role="status">{status || '正在整理盘面重点'}<span className="typing-cursor" aria-hidden="true" /></p>
              </div>
            </article>
          </>
        )}
        <div ref={conversationEndRef} aria-hidden="true" />
      </div>

      {turns.length > 0 && (
        <div className="ai-follow-up" role="group" aria-label="基于上一轮继续追问">
          <div className="ai-follow-up-heading">
            <label htmlFor="ai-follow-up-input">继续追问</label>
            <span>会接着上面的内容回答</span>
          </div>
          <div className="ai-follow-up-composer">
            <textarea
              id="ai-follow-up-input"
              aria-label="继续追问"
              aria-describedby="ai-follow-up-hint"
              value={followUpQuestion}
              onChange={(event) => onFollowUpQuestionChange(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder="接着问这件事……"
              rows={2}
              maxLength={300}
              disabled={loading}
            />
            <button type="button" className="ai-follow-up-submit" onClick={onFollowUp} disabled={loading || !followUpQuestion.trim()}>
              <Icon name="spark" size={16} />追问
            </button>
          </div>
          <div className="ai-follow-up-footer">
            <div className="ai-follow-up-presets" aria-label="快捷追问">
              {FOLLOW_UP_PRESETS.map((item) => (
                <button key={item.label} type="button" disabled={loading} onClick={() => onFollowUpQuestionChange(item.value)}>{item.label}</button>
              ))}
            </div>
            <span id="ai-follow-up-hint">Enter 发送 · Shift+Enter 换行</span>
          </div>
        </div>
      )}
    </section>
  );
}
